import React from "react";
import { Link } from "react-router-dom";
import { Rocket, Truck, Users, Building2, TrendingUp } from "lucide-react";

const ROCKET = "https://customer-assets.emergentagent.com/job_lifted-universe/artifacts/7o00drme_LiftedRocket2020.png";
const SUPPLIES_LOGO = "https://customer-assets.emergentagent.com/job_lifted-universe/artifacts/abml72sv_49C6879E-36AA-4230-8A4A-723A845ADABF.png";

const ROLES = [
  { id: "affiliate", icon: Rocket, label: "Affiliate", title: "Promote. Refer. Earn. Grow.",
    bullets: ["$40/month to join", "Referral-based growth system", "Earn commissions", "Levels unlock via referrals"],
    cta: "Join the Affiliate Network", to: "/affiliate-zone" },
  { id: "drivers", icon: Truck, label: "Driver", title: "Deliver. Earn. Impact.",
    bullets: ["Flexible delivery opportunity", "Help power same-day delivery", "Serve customers in your area", "Be part of Lifted Supplies"],
    cta: "Apply as Driver", to: "/auth/register" },
  { id: "partners", icon: Users, label: "Partner", title: "Collaborate. Expand. Win.",
    bullets: ["For smoke shops + related businesses", "Add delivery to your store", "Boost visibility through affiliates", "Grow together"],
    cta: "Become a Partner", to: "/auth/register" },
  { id: "franchise", icon: Building2, label: "Franchisee", title: "Own. Operate. Build.",
    bullets: ["$40,000 franchise investment", "Own your territory", "Built-in Lifted Supplies delivery", "Training + operational support"],
    cta: "Apply as Franchisee", to: "/auth/register" },
];

export default function LiftedOpportunity() {
  return (
    <div data-testid="lifted-opportunity" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-12">
      <header className="text-center max-w-3xl mx-auto mb-16">
        <div className="eyebrow flex items-center justify-center gap-2"><img src={ROCKET} alt="" className="h-6 w-6" /> Lifted Ecosystem</div>
        <h1 className="font-anton text-4xl sm:text-6xl uppercase mt-4 leading-[0.95]">More than a brand.<br /><span className="text-lifted-green">A connected ecosystem built around opportunity.</span></h1>
        <p className="text-lifted-dim mt-5">LiftedAtTheMoment and Lifted Supplies are building a connected ecosystem designed to help creators, businesses, affiliates, drivers, partners, and future franchise operators grow together — through visibility, networking, ecommerce, delivery, and expansion.</p>
      </header>

      {/* Four-role cards */}
      <section className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-16">
        {ROLES.map((r) => (
          <article key={r.id} id={r.id} data-testid={`opportunity-role-${r.id}`} className="glass-panel p-6 sm:p-8">
            <r.icon className="h-7 w-7 text-lifted-green" />
            <div className="eyebrow !text-lifted-text-dim mt-3">Become a</div>
            <h2 className="font-anton text-3xl uppercase mt-1">{r.label}</h2>
            <p className="text-lifted-text mt-2">{r.title}</p>
            <ul className="mt-4 space-y-1.5 text-sm text-lifted-dim">
              {r.bullets.map((b) => <li key={b} className="flex items-start gap-2"><span className="text-lifted-green">▸</span> {b}</li>)}
            </ul>
            <Link to={r.to} className="btn-lifted mt-5 inline-flex" data-testid={`opportunity-cta-${r.id}`}>{r.cta}</Link>
          </article>
        ))}
      </section>

      {/* The loop */}
      <section className="mb-16" data-testid="opportunity-loop">
        <div className="text-center">
          <div className="eyebrow">The Loop</div>
          <h2 className="font-anton text-3xl sm:text-4xl uppercase mt-2">How the Lifted ecosystem connects</h2>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mt-8">
          {[
            { t: "Customers Shop", d: "Demand drives the loop." },
            { t: "You Sell",       d: "Premium Lifted products." },
            { t: "We Deliver",     d: "Same-day where available." },
            { t: "You Profit",     d: "Keep what you earn." },
            { t: "We Grow",        d: "Ecosystem compounds." },
          ].map((s, i) => (
            <div key={s.t} className="glass-panel p-5">
              <div className="text-[0.6rem] font-bebas tracking-[0.18em] text-lifted-mute uppercase">{String(i + 1).padStart(2, "0")}</div>
              <div className="font-anton uppercase text-base mt-1">{s.t}</div>
              <div className="text-xs text-lifted-dim mt-1">{s.d}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Why people are paying attention */}
      <section className="glass-panel p-8 sm:p-12 mb-16" data-testid="opportunity-why">
        <div className="text-center">
          <div className="eyebrow flex items-center justify-center gap-2"><TrendingUp className="h-3 w-3" /> Why people are paying attention</div>
          <h2 className="font-anton text-3xl uppercase mt-2">Multiple ways to win.</h2>
        </div>
        <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-3 text-center text-sm">
          {[
            { n: "Community", d: "Real people. Real impact." },
            { n: "Orders",    d: "Delivery in motion." },
            { n: "Nationwide vision", d: "Coast to coast growth." },
            { n: "Countless paths",   d: "Multiple ways to win." },
          ].map((c) => (
            <div key={c.n}>
              <div className="font-anton uppercase text-lifted-green">{c.n}</div>
              <div className="text-xs text-lifted-dim mt-1">{c.d}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Powered by Lifted Supplies */}
      <section className="text-center mb-16" data-testid="opportunity-powered">
        <div className="text-xs text-lifted-mute font-bebas tracking-[0.18em] uppercase">Powered by</div>
        <img src={SUPPLIES_LOGO} alt="Lifted Supplies" className="h-16 mx-auto mt-3" />
        <p className="text-lifted-dim mt-2 max-w-xl mx-auto text-sm">The wholesale, delivery, and operations engine of the Lifted ecosystem.</p>
      </section>

      {/* Closer */}
      <section className="text-center max-w-3xl mx-auto" data-testid="opportunity-closer">
        <h3 className="font-anton text-3xl sm:text-4xl uppercase">This is more than a business.<br />It's a movement. It's a legacy.</h3>
        <p className="text-lifted-dim mt-4">One brand. One community. Unlimited possibilities.</p>
        <div className="mt-6 flex flex-wrap gap-3 justify-center">
          <Link to="/affiliate-zone" className="btn-lifted">Join as Affiliate</Link>
          <Link to="/auth/register" className="btn-ghost">Create account first</Link>
        </div>
      </section>
    </div>
  );
}
